/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import React, { type FC, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';

import Avatar from '@atlaskit/avatar';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Stack, xcss, Text } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ExternalCollaboratorUserLozenge } from '@confluence/external-collab-ui/entry-points/ExternalUserLozengeLoader';
import { usePageContentId } from '@confluence/page-context';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';

import type { Principal, KnownErrors, Role } from '../../../../commonTypes';
import { getKnownErrors } from '../../../../../controller/queries/getContentRestrictions';
import { SitePermissionType } from '../../../../../controller/queries/__types__/ShareAndRestrictDialogQuery';
import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../../../../controller/Controller';

import type {
	UserDetailsPrincipalDetailsKnownErrorsQuery as UserDetailsPrincipalDetailsKnownErrorsQuery$data,
	UserDetailsPrincipalDetailsKnownErrorsQueryVariables as UserDetailsPrincipalDetailsKnownErrorsQuery$variables,
} from './__types__/UserDetailsPrincipalDetailsKnownErrorsQuery';
import { PrincipalDetailsWarningMessage } from './PrincipalDetailsWarningMessage';
import {
	ParentAccessWarning,
	SpaceAccessWarning,
	DraftAccessWarning,
	PendingSiteAccessWarning,
} from './warningMessages/index';

const UserDetailsPrincipalDetailsKnownErrorsQuery = gql`
	query UserDetailsPrincipalDetailsKnownErrorsQuery($accountId: String, $contentId: ID) {
		userWithContentRestrictions(accountId: $accountId, contentId: $contentId) {
			hasSpaceEditPermission
			hasSpaceViewPermission
			restrictingContent {
				id
				links {
					webui
					base
				}
			}
		}
	}
`;
export type PrincipalDetailsProps = {
	principal: Principal;
	isYou: boolean;
	role: Role;
	updateKnownErrors?: (id: string, errors: KnownErrors) => void;
};

const i18n = defineMessages({
	isYouText: {
		id: 'share-and-restrict-dialog.principal-details.you-text',
		defaultMessage: '(You)',
		description:
			'Label indicating that the user is the current user, shown as "Jane Rotanson (You)"',
	},
});

const lozengeStyles = xcss({
	paddingLeft: 'space.100',
});

const principalDetailsStyle = xcss({
	overflowWrap: 'anywhere',
});

type UserDetailsProps = {
	id: string;
	avatarUrl: string;
	name: string;
	email: string;
	knownErrors: KnownErrors;
	sitePermissionType: string;
	isYou: boolean;
	role: Role;
	updateKnownErrors?: (id: string, errors: KnownErrors) => void;
	isDraft: boolean;
};

export const UserDetails: FC<UserDetailsProps> = ({
	id,
	avatarUrl,
	name,
	email,
	knownErrors,
	sitePermissionType,
	isYou,
	role,
	updateKnownErrors,
	isDraft,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const [contentId] = usePageContentId();
	const { data, loading, error } = useQuery<
		UserDetailsPrincipalDetailsKnownErrorsQuery$data,
		UserDetailsPrincipalDetailsKnownErrorsQuery$variables
	>(UserDetailsPrincipalDetailsKnownErrorsQuery, {
		skip: knownErrors !== undefined || isYou,
		variables: {
			accountId: id,
			contentId,
		},
		fetchPolicy: 'network-only',
	});
	useEffect(() => {
		if (error) {
			markErrorAsHandled(error);
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					action: 'errored',
					actionSubject: 'principalDetailsQuery',
					attributes: {
						message: error?.message,
						name: error?.name,
						stack: error?.stack,
						cause: error?.cause,
					},
				},
			}).fire();
		}
	}, [createAnalyticsEvent, error]);

	function getErrors(): KnownErrors {
		if (knownErrors) {
			return knownErrors;
		}

		if (data) {
			const { userWithContentRestrictions } = data;
			const {
				hasSpaceEditPermission = null,
				hasSpaceViewPermission = null,
				restrictingContent = null,
			} = userWithContentRestrictions || {};

			const newErrors = getKnownErrors({
				hasSpaceEditPermission,
				hasSpaceViewPermission,
				restrictingContent,
			});

			if (newErrors && updateKnownErrors) {
				updateKnownErrors(id, newErrors);
			}
			return newErrors;
		}

		return undefined;
	}

	const errors = getErrors();

	const [shouldRenderWarningMessage, warningMessage] = (() => {
		if (errors?.accessWarning === 'parent-access-warning') {
			return [
				true,
				<ParentAccessWarning key={errors.accessWarning} parentLink={errors.parentLink} />,
			];
		}
		if (errors?.accessWarning === 'space-view-access-warning') {
			return [
				true,
				<SpaceAccessWarning key={errors.accessWarning} accessWarning="space-view-access-warning" />,
			];
		}
		if (errors?.accessWarning === 'space-edit-access-warning' && role !== 'can-view') {
			return [
				true,
				<SpaceAccessWarning key={errors.accessWarning} accessWarning="space-edit-access-warning" />,
			];
		}
		if (errors?.accessWarning === 'pending-site-access-warning') {
			return [true, <PendingSiteAccessWarning key={errors.accessWarning} />];
		}
		if (isDraft && role !== 'can-edit') {
			return [true, <DraftAccessWarning key="draft-edit-access-warning" />];
		}
		return [false, null];
	})();

	return (
		<Inline space="space.100" alignBlock="center">
			<Avatar src={avatarUrl} isDisabled={shouldRenderWarningMessage} />
			<Stack>
				<Stack xcss={principalDetailsStyle}>
					<Inline>
						<Text color={shouldRenderWarningMessage ? 'color.text.subtlest' : 'color.text'}>
							{name}
							{isYou && ` ${formatMessage(i18n.isYouText)}`}
						</Text>
						{sitePermissionType === SitePermissionType.EXTERNAL && (
							<Box xcss={lozengeStyles}>
								<ExternalCollaboratorUserLozenge />
							</Box>
						)}
					</Inline>
					<Text size="small" color="color.text.subtlest">
						{email}
					</Text>
				</Stack>
				{shouldRenderWarningMessage && !loading && warningMessage && (
					<PrincipalDetailsWarningMessage warningMessage={warningMessage} />
				)}
			</Stack>
		</Inline>
	);
};
